import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React from 'react';
import PropTypes from '../../../node_modules/prop-types/index.js';
import { KEY_CODE } from '../constants.js';
import Icon from '../Icon/Icon.js';
import { safeRest, noop, rem } from '../../utils/functions.js';
import handle from '../../utils/handle.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import { EXPANDABLE_PANEL_VARIANT } from './constants.js';
import { HeaderWrapper, HeaderTitle } from './styledComponents.js';
import useTheme from '../../hooks/useTheme.js';

var _excluded = ["id", "expandItem", "isExpanded", "title", "titleAs", "onToggle", "ariaLabel", "customStyle", "variant", "ariaControls", "expandIcon", "collapseIcon"];

var getIcon = function getIcon(isExpanded, expandIcon, collapseIcon, variant, theme) {
  if (variant === EXPANDABLE_PANEL_VARIANT.PRIMARY && !isExpanded) {
    return expandIcon || /*#__PURE__*/React.createElement(Icon, {
      manulifeName: "plus_withcircle",
      fill: theme.colorPalette.neutralColors.light.shade_white,
      secondaryColor: theme.colorPalette.accentColor.shade_default
    });
  }

  if (variant === EXPANDABLE_PANEL_VARIANT.PRIMARY && isExpanded) {
    return collapseIcon || /*#__PURE__*/React.createElement(Icon, {
      manulifeName: "minus_withcircle",
      fill: theme.colorPalette.accentColor.shade_default,
      secondaryColor: theme.colorPalette.accentColor.shade_default
    });
  }

  if (variant === EXPANDABLE_PANEL_VARIANT.SECONDARY && !isExpanded) {
    return expandIcon || /*#__PURE__*/React.createElement(Icon, {
      manulifeName: "plus",
      fill: theme.colorPalette.accentColor.shade_default,
      height: rem(12),
      width: rem(12)
    });
  } // Else variant === EXPANDABLE_PANEL_VARIANT.SECONDARY && isExpanded


  return collapseIcon || /*#__PURE__*/React.createElement(Icon, {
    manulifeName: "minus",
    fill: theme.colorPalette.accentColor.shade_default,
    height: rem(12),
    width: rem(12)
  });
};

var Header = function Header(_ref) {
  var id = _ref.id,
      expandItem = _ref.expandItem,
      isExpanded = _ref.isExpanded,
      title = _ref.title,
      titleAs = _ref.titleAs,
      onToggle = _ref.onToggle,
      ariaLabel = _ref.ariaLabel,
      customStyle = _ref.customStyle,
      variant = _ref.variant,
      ariaControls = _ref.ariaControls,
      expandIcon = _ref.expandIcon,
      collapseIcon = _ref.collapseIcon,
      rest = _objectWithoutProperties(_ref, _excluded);

  var theme = useTheme();

  var _safeCustomStyle = safeCustomStyle(customStyle),
      headerWrapperStyle = _safeCustomStyle.headerWrapperStyle,
      titleStyle = _safeCustomStyle.titleStyle;

  var safeProps = safeRest(rest);
  return /*#__PURE__*/React.createElement(HeaderWrapper, _extends({}, safeProps, {
    id: id,
    role: "button",
    "aria-label": ariaLabel,
    "aria-expanded": isExpanded,
    "aria-controls": ariaControls,
    tabIndex: 0,
    headerWrapperStyle: headerWrapperStyle,
    onClick: function onClick(e) {
      handle(onToggle)(e, isExpanded);
      expandItem();
    },
    onKeyDown: function onKeyDown(e) {
      if (e.keyCode === KEY_CODE.SPACE || e.keyCode === KEY_CODE.ENTER) {
        handle(onToggle)(e, isExpanded);
        expandItem();
      }
    }
  }), getIcon(isExpanded, expandIcon, collapseIcon, variant, theme), /*#__PURE__*/React.createElement(HeaderTitle, {
    as: titleAs,
    variant: variant,
    titleStyle: titleStyle,
    "data-testid": "".concat(id, "-title")
  }, title));
};

process.env.NODE_ENV !== "production" ? Header.propTypes = {
  /** id for header wrapper */
  id: PropTypes.string,

  /** title label for header */
  title: PropTypes.node,
  onToggle: PropTypes.func,

  /** Aria-Label */
  ariaLabel: PropTypes.string,

  /** idREF for aria-controls attribute */
  ariaControls: PropTypes.string,

  /** expand item function */
  expandItem: PropTypes.any,
  // eslint-disable-line react/forbid-prop-types

  /** current state of expansion panel */
  isExpanded: PropTypes.bool,

  /** Icon component to pass for collapsed state */
  expandIcon: PropTypes.node,

  /** Icon component to pass for expanded state */
  collapseIcon: PropTypes.node,

  /** Which style of expandable panel to use */
  variant: PropTypes.oneOf([EXPANDABLE_PANEL_VARIANT.PRIMARY, EXPANDABLE_PANEL_VARIANT.SECONDARY]),

  /** style overrides for header */
  customStyle: PropTypes.shape({
    headerWrapperStyle: PropTypes.object,
    titleStyle: PropTypes.object
  })
} : void 0;
Header.defaultProps = {
  id: undefined,
  title: '',
  onToggle: noop,
  ariaLabel: '',
  ariaControls: undefined,
  isExpanded: false,
  expandItem: undefined,
  expandIcon: undefined,
  collapseIcon: undefined,
  variant: EXPANDABLE_PANEL_VARIANT.PRIMARY,
  customStyle: {
    headerWrapperStyle: {},
    titleStyle: {}
  }
};
process.env.NODE_ENV !== "production" ? Header.displayName = 'Header' : void 0;
var Header$1 = Header;

export { Header$1 as default };
